/**
 * 登入使用者資料
 * -----------------------------------------------------------------------------
 * 對齊後端 ``LoginResult.profile`` schema（snake_case），前端轉成 camelCase。
 *
 * 重要：
 * - **使用者識別碼**是 ``LoginResult.creativeUid``（不在 ``UserProfile`` 內）。
 *   後端的 ``permission-service`` 內部 user_id 永遠不暴露給前端。
 * - 前端任何 API 用到使用者 id，**一律用 ``creativeUid``**。
 *
 * OIDC 流程：前端取 Google ID Token → POST `/api/front_auth/google_login`
 *           → 後端驗 aud/iss/exp → 回應自家 session（HttpOnly cookie + JWT）+ profile。
 */

/**
 * 後端 ``profile`` 直送過來的原始 JSON（snake_case）。
 * 通常只在 HTTP adapter 內出現，業務層請使用 :type:`UserProfile`。
 */
export interface UserProfileDto {
  email: string;
  display_name: string | null;
  photo_url: string | null;
  locale: string;
  timezone: string;
  role: 'user' | 'admin' | 'manager';
  last_login_datetime: string | null;
  created_datetime: string | null;
  updated_datetime: string | null;
  // R45 Beta v0.0.1(spec §8 UserProfile)— group tenancy 欄位;BE 漸進 ship
  // 三層 fallback(spec §2.2):stored localStorage → current_group_id → personal_group_id
  personal_group_id?: string | null;
  current_group_id?: string | null;
}

/**
 * 前端使用的使用者 profile 型別（camelCase）。
 */
export interface UserProfile {
  email: string;
  /** ``null`` 時 :func:`mapUserProfileDto` 會 fallback 到 email 的 local-part。 */
  displayName: string;
  photoUrl: string | null;
  locale: string;
  timezone: string;
  role: 'user' | 'admin' | 'manager';
  lastLoginDatetime: string | null;
  createdDatetime: string | null;
  updatedDatetime: string | null;
  // R45 Beta v0.0.1(spec §8)— group tenancy 三層 fallback
  /** Personal group(`is_personal=true`)— `null` 表示舊 BE 端尚未回欄。 */
  personalGroupId: string | null;
  /** User 上次選擇的 group — `null` 表示舊 BE 端尚未回欄;FE fallback 走 personal。 */
  currentGroupId: string | null;
}

/**
 * 將後端 `UserProfileDto` 轉為前端 `UserProfile`。
 *
 * @param dto - 後端 snake_case 原始 JSON
 * @returns camelCase 結構;`display_name` 為 `null` 時 fallback 到 email 的 local-part
 */
export function mapUserProfileDto(dto: UserProfileDto): UserProfile {
  const fallbackName = dto.email.split('@')[0] ?? dto.email;
  return {
    email: dto.email,
    displayName: dto.display_name ?? fallbackName,
    photoUrl: dto.photo_url,
    locale: dto.locale,
    timezone: dto.timezone,
    role: dto.role,
    lastLoginDatetime: dto.last_login_datetime,
    createdDatetime: dto.created_datetime,
    updatedDatetime: dto.updated_datetime,
    personalGroupId: dto.personal_group_id ?? null,
    currentGroupId: dto.current_group_id ?? null,
  };
}
